/**
 * @fileoverview Loads the student's deliverable detail and exposes the submit
 * action. Backend contract: GET /api/student_tasks/:id → PUT
 * /api/student_task_deliveries/:deliveryId. Links (AR-677/AR-678): POST/DELETE
 * /api/student_task_deliveries/:deliveryId/links on the SAME effective
 * delivery id; the local list is hook state (no refetch — a new GET would
 * create another throwaway open delivery, AR-741).
 */

import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { dataSource } from '../dataSource'
import type { AddLinkOutcome } from '../deliverableDrawer/linkUtils'
import type { DeliverableDetail, DeliveryLink } from '../types'
import { useTaskDetail } from './useTaskDetail'

interface UseStudentDeliverableResult {
  loading: boolean
  errored: boolean
  submitting: boolean
  /**
   * True when a done delivery already exists OR a submit just succeeded in
   * this session (covers the window between the PUT and the reload, so a
   * second upload/link never re-PUTs — mirrors the TaskTracker twin).
   */
  submitted: boolean
  data: DeliverableDetail | null
  links: readonly DeliveryLink[]
  submit: (description?: string) => Promise<boolean>
  addLink: (url: string) => Promise<AddLinkOutcome>
  removeLink: (linkId: string) => Promise<boolean>
}

export function useStudentDeliverable(
  identifierId: string,
  opened: boolean,
): UseStudentDeliverableResult {
  const { loading, errored, data, reload } = useTaskDetail(
    dataSource.fetchDeliverable,
    identifierId,
    opened,
  )
  const [submitting, setSubmitting] = useState(false)
  const [justSubmitted, setJustSubmitted] = useState(false)
  const [links, setLinks] = useState<readonly DeliveryLink[]>([])

  // Fresh flag per drawer open/task (mirrors the TaskTracker twin).
  useEffect(() => {
    if (opened) setJustSubmitted(false)
  }, [opened, identifierId])

  // Seed the local list from the loaded detail (effective delivery, AR-741).
  useEffect(() => {
    setLinks(data?.links ?? [])
  }, [data])

  const submit = useCallback(
    async (description = ''): Promise<boolean> => {
      if (!data?.studentTaskDeliveryId) return false
      setSubmitting(true)
      try {
        await dataSource.submitDeliverable(data, description)
        setJustSubmitted(true)
        reload()
        return true
      } catch (error) {
        console.error('Error submitting deliverable:', error)
        return false
      } finally {
        setSubmitting(false)
      }
    },
    [data, reload],
  )

  // Mirrors the AR-677 contract: 422 = invalid/broken URL (inline error in the
  // modal); anything else = 'error' (parent toasts). Cache is invalidated by
  // the data source; the local list is updated in place (no refetch).
  const addLink = useCallback(
    async (url: string): Promise<AddLinkOutcome> => {
      if (!data?.studentTaskDeliveryId || !url) return 'error'
      try {
        const link = await dataSource.addDeliverableLink(data, url)
        if (link?.id) setLinks(current => [...current, link])
        return 'success'
      } catch (error) {
        console.error('Error adding deliverable link:', error)
        if (axios.isAxiosError(error) && error.response?.status === 422) return 'invalid'
        return 'error'
      }
    },
    [data],
  )

  const removeLink = useCallback(
    async (linkId: string): Promise<boolean> => {
      if (!data?.studentTaskDeliveryId || !linkId) return false
      try {
        await dataSource.removeDeliverableLink(data, linkId)
        setLinks(current => current.filter(link => link.id !== linkId))
        return true
      } catch (error) {
        console.error('Error removing deliverable link:', error)
        return false
      }
    },
    [data],
  )

  const submitted = justSubmitted || Boolean(data && (data.done || data.lastDelivery))

  return { loading, errored, submitting, submitted, data, links, submit, addLink, removeLink }
}
